/* eslint-disable max-lines-per-function, max-statements, no-undef */
import React, { useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ARTIFACT_SHARE_LABELS, openArtifactShare } from '../../utils/artifactSharing';

import { ActionBarAnalyticsScope } from './actionBarAnalyticsScope';
import type { GeneratedArtifactShare } from './generatedArtifactShares';

import './ArtifactShareMenu.css';

interface ArtifactShareMenuProps {
  projectId: string;
  actions: GeneratedArtifactShare[];
}

/** Ready artifacts belong to the project, independently of the current workflow message. */
export function ArtifactShareMenu({ projectId, actions }: ArtifactShareMenuProps) {
  const analyticsScope = useContext(ActionBarAnalyticsScope);
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const initialFocus = useRef<'first' | 'last'>('first');
  const readiness = JSON.stringify([projectId, actions.map(({ artifactType, itemId, completedAt }) => (
    [artifactType, itemId, completedAt]
  ))]);
  const latestReadiness = useRef(readiness);

  useLayoutEffect(() => { latestReadiness.current = readiness; }, [readiness]);
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  const [previousReadiness, setPreviousReadiness] = useState(readiness);

  if (previousReadiness !== readiness) {
    setPreviousReadiness(readiness);
    setOpenedFor(null);
  }
  const [position, setPosition] = useState({ left: 12, top: 12, width: 210, maxHeight: 360 });
  const menuOpen = Boolean(projectId && actions.length && openedFor === readiness);
  const close = useCallback((restoreFocus = false) => {
    setOpenedFor(null);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const placeMenu = useCallback(() => {
    const trigger = triggerRef.current;

    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const inset = 12;
    const width = Math.min(300, Math.max(210, rect.width), window.innerWidth - inset * 2);
    const maxHeight = Math.min(360, Math.max(0, window.innerHeight - inset * 2));
    const height = Math.min(maxHeight, menuRef.current?.scrollHeight || actions.length * 40 + 14);
    // Opens upward, away from the composer it sits on; downward only when there is no room above.
    const above = rect.top - height - 7;
    const top = above >= inset ? above : rect.bottom + 7;

    setPosition({
      width, maxHeight,
      left: Math.max(inset, Math.min(rect.left, window.innerWidth - width - inset)),
      top: Math.max(inset, Math.min(top, window.innerHeight - height - inset)),
    });
  }, [actions.length]);

  useLayoutEffect(() => {
    if (!menuOpen) return undefined;
    placeMenu();
    const items = menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');

    items?.[initialFocus.current === 'last' ? items.length - 1 : 0]?.focus();
    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);

    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [menuOpen, placeMenu]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const dismissOutside = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)
        && !triggerRef.current?.contains(event.target as Node)) close();
    };
    const dismissKeyboard = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      } else if (event.key === 'Tab') close();
    };

    document.addEventListener('pointerdown', dismissOutside);
    document.addEventListener('keydown', dismissKeyboard);

    return () => {
      document.removeEventListener('pointerdown', dismissOutside);
      document.removeEventListener('keydown', dismissKeyboard);
    };
  }, [menuOpen, close]);

  const navigateMenu = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    const targets: Record<string, number> = {
      ArrowDown: (current + 1) % items.length,
      ArrowUp: (current - 1 + items.length) % items.length,
      Home: 0,
      End: items.length - 1,
    };

    if (targets[event.key] === undefined) return;
    event.preventDefault();
    items[targets[event.key]]?.focus();
  };

  if (!projectId || !actions.length) return null;

  return <div className="artifact-share-menu" data-action-overflow-control="true">
    <button
      ref={triggerRef}
      type="button"
      className="ai-overview-action-btn ai-overview-action-btn--share artifact-share-menu__trigger"
      data-action-placement="pinned"
      aria-label="Share"
      aria-haspopup="menu"
      aria-expanded={menuOpen}
      aria-controls={menuOpen ? menuId : undefined}
      onClick={() => {
        initialFocus.current = 'first';
        setOpenedFor(menuOpen ? null : readiness);
      }}
      onKeyDown={(event) => {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        event.preventDefault();
        initialFocus.current = event.key === 'ArrowUp' ? 'last' : 'first';
        setOpenedFor(readiness);
      }}
    >
      <span className="ai-overview-action-btn__label">Share</span>
      <i className="fa-solid fa-chevron-down artifact-share-menu__chevron" aria-hidden="true" />
    </button>
    {menuOpen && createPortal(<div
      ref={menuRef}
      id={menuId}
      className="artifact-share-menu__menu"
      data-action-analytics-owner={analyticsScope}
      role="menu"
      aria-label="Share"
      style={position}
      onKeyDown={navigateMenu}
    >
      {actions.map((action) => <button
        key={`${action.artifactType}:${action.itemId || ''}`}
        className="artifact-share-menu__item"
        type="button"
        role="menuitem"
        onClick={() => {
          if (latestReadiness.current !== readiness) return;
          close(true);
          openArtifactShare({ projectId, artifactType: action.artifactType, itemId: action.itemId });
        }}
      >
        <i className="fa-solid fa-share-nodes" aria-hidden="true" />
        <span>Share {ARTIFACT_SHARE_LABELS[action.artifactType]}</span>
      </button>)}
    </div>, document.body)}
  </div>;
}
